import React from 'react'

// DAG(dataset_build)가 보고하는 단계. DVC 스토어 도입으로 빌드 시 ZIP 을 만들지
// 않으므로 'packaging' 대신 파일 기록(writing) → 스토어 적재(storing) 로 나뉜다.
// 'packaging' 은 이전 빌드가 남긴 값을 위해 유지한다.
type Step = 'prepare' | 'writing' | 'storing' | 'complete' | 'failed'

interface Props {
  isOpen: boolean
  onClose: () => void
  status: { status: string; step?: string; progress?: number; error?: string | null }
}

// 초기화·데이터 매칭·샘플링은 순식간에 지나가 따로 보여줄 의미가 없어 '준비' 하나로 묶는다.
// (DAG 는 디버깅을 위해 세 단계를 그대로 보고하고, 표시만 합친다)
const order: Step[] = ['prepare', 'writing', 'storing', 'complete']
const stepAlias: Record<string, Step> = {
  init: 'prepare', match: 'prepare', sampling: 'prepare',
  packaging: 'writing',  // 예전 빌드가 남긴 값
}

export default function DatasetProgressModal({ isOpen, onClose, status }: Props) {
  if (!isOpen) return null
  const raw = (status.step as string) || 'init'
  const currentStep = (stepAlias[raw] || raw) as Step
  const currentIdx = status.status === 'failed'
    ? Math.max(0, order.indexOf(currentStep))
    : Math.max(0, order.indexOf(currentStep))

  const Dot = ({ idx, label }: { idx: number; label: string }) => (
    <div className="flex items-center">
      <div className={`w-3 h-3 rounded-full mr-2 ${idx < currentIdx ? 'bg-green-600' : idx === currentIdx ? 'bg-black' : 'bg-gray-300'}`}></div>
      <span className={`text-sm ${idx < currentIdx ? 'text-green-700' : idx === currentIdx ? 'text-black' : 'text-gray-500'}`}>{label}</span>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">학습데이터셋 생성 진행</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <Dot idx={0} label="준비 (매칭 · 샘플링)" />
          <Dot idx={1} label="파일 기록" />
          <Dot idx={2} label="스토어 적재" />
          <Dot idx={3} label={status.status === 'failed' ? '실패' : '완료'} />
        </div>

        <div className="p-3 rounded bg-gray-50 text-sm text-gray-700">
          <div>상태: {status.status}{status.step ? ` · ${status.step}` : ''}{typeof status.progress === 'number' ? ` · ${status.progress}%` : ''}</div>
          {status.status === 'failed' && (
            <div className="mt-1 text-red-700">
              {status.error ? (
                <>
                  <div className="font-medium">오류:</div>
                  <div className="whitespace-pre-wrap break-all">{status.error}</div>
                </>
              ) : (
                '작업에 실패했습니다. 설정을 확인하고 다시 시도하세요.'
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


